/**
 * Replaces placeholders like {0} in the given string with the arguments at the matching position.
 * @param str The input string with placeholders, e.g. "Hello {0}"
 * @param args The sorted arguments matching the placeholders, e.g. "World"
 * @example stringFormat("Hello {0} {1}", "beautiful", "World!") // returns "Hello beautiful World!"
 * @example stringFormat("Hello {0}, {0}", "beauty", "World") // returns "Hello beauty, beauty!"
 * @example stringFormat("Hello {1}, {0}", "beauty", "World") // returns "Hello World, beauty!"
 */
export const stringFormat = (str: string, ...args: string[]) => {
  if (!str) {
    return "";
  }
  return str.replace(/{(\d+)}/g, (_match, index) => args[index] || "");
};

export default stringFormat;

export const truncateHtml = (text, length) => {
  var truncated = text.substring(0, text.lastIndexOf(" ", length + eliminateHTMLTagCount(text)));
  // Remove line breaks and surrounding whitespace
  truncated = truncated.replace(/(\r\n|\n|\r)/gm, "").trim();
  // If the text ends with an incomplete start tag, trim it off
  truncated = truncated.replace(/<(\w*)(?:(?:\s\w+(?:={0,1}(["']{0,1})\w*\2{0,1})))*$/g, "");
  // If the text ends with a truncated end tag, fix it.
  var truncatedEndTagExpr = /<\/((?:\w*))$/g;
  var truncatedEndTagMatch = truncatedEndTagExpr.exec(truncated);
  if (truncatedEndTagMatch != null) {
    var truncatedEndTag = truncatedEndTagMatch[1];
    // Check to see if there's an identifiable tag in the end tag
    if (truncatedEndTag.length > 0) {
      // If so, find the start tag, and close it
      var startTagExpr = new RegExp("<(" + truncatedEndTag + "\\w?)(?:(?:\\s\\w+(?:=([\"'])\\w*\\2)))*>");
      var testString = truncated;
      var startTagMatch = startTagExpr.exec(testString);

      var startTag = null;
      while (startTagMatch != null) {
        startTag = startTagMatch[1];
        testString = testString.replace(startTagExpr, "");
        startTagMatch = startTagExpr.exec(testString);
      }
      if (startTag != null) {
        truncated = truncated.replace(truncatedEndTagExpr, "</" + startTag + ">");
      }
    } else {
      // Otherwise, cull off the broken end tag
      truncated = truncated.replace(truncatedEndTagExpr, "");
    }
  }
  // Now the tricky part. Reverse the text, and look for opening tags. For each opening tag,
  //  check to see that he closing tag before it is for that tag. If not, append a closing tag.
  var testString = reverseHtml(truncated);
  var reverseTagOpenExpr = /<(?:(["'])\w*\1=\w+ )*(\w*)>/;
  var tagMatch = reverseTagOpenExpr.exec(testString);
  while (tagMatch != null) {
    var tag = tagMatch[0];
    var tagName = tagMatch[2];
    var startPos = tagMatch.index;
    var endPos = startPos + tag.length;
    var fragment = testString.substring(0, endPos);
    // Test to see if an end tag is found in the fragment. If not, append one to the end
    //  of the truncated HTML, thus closing the last unclosed tag
    if (!new RegExp("<" + tagName + "/>").test(fragment)) {
      truncated += "</" + reverseHtml(tagName) + ">";
    }
    // Get rid of the already tested fragment
    testString = testString.replace(fragment, "");
    // Get another tag to test
    tagMatch = reverseTagOpenExpr.exec(testString);
  }
  return truncated;
};

function eliminateHTMLTagCount(text) {
  const startTag = text.match(new RegExp(/(<([^>]+)>)/gi.exec(text)[0], "gi"));
  const endTag = text.match(new RegExp(/(<[/]([^>]+)>)/gi.exec(text)[0], "gi"));
  // Total count of start and end tags in a string
  const startTagCount = startTag.length;
  const endTagCount = endTag.length;
  // Total character count of start and end tags in a string
  const startTagCharLength = startTag[0].length * startTagCount;
  const endTagCharLength = endTag[0].length * endTagCount;
  // Overall character count of both tags in a string
  const totalTagLengthCount = startTagCharLength + endTagCharLength;
  return totalTagLengthCount;
}

function reverseHtml(str) {
  var ph = String.fromCharCode(206);
  var result = str.split("").reverse().join("");
  while (result.indexOf("<") > -1) {
    result = result.replace("<", ph);
  }
  while (result.indexOf(">") > -1) {
    result = result.replace(">", "<");
  }
  while (result.indexOf(ph) > -1) {
    result = result.replace(ph, ">");
  }
  return result;
}

export const stripHtml = (htmlString: string) => {
  return htmlString.replace(/<\/?[^>]+(>|$)/g, "");
};

/**
 *  Shortens a string without cutting words
 * @param str The string to be shorten
 * @param maxlength max length of the string
 * @param endChars charakters to be added to the shortened string ( "..." for example)
 */
export const shortenString = (str, maxlength, endChars) => {
  return str.substring(0, str.lastIndexOf(" ", maxlength)) + endChars;
};

/**
 * remove protocol and third-level-domain (subdomain) from link
 * @param link {string}
 */
export const shortenLink = (link: string) => {
  return link.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "");
};

/**
 * Converts a string to a CONSTANT_CASE
 * @param str str to be converted
 * @returns the converted string
 */
export const toConstantCase = (str: string): string => {
  //replace whitespace with underscore
  str = replaceAll(str, " ", "_");
  //replace "-" if it was dashCase
  str = replaceAll(str, "-", "_");
  let positions = [];
  //get position of every uppercase character
  for (var i = 0; i < str.length; i++) {
    if (str[i].match(/[A-Z]/) != null) {
      positions.push(i);
    }
  }
  let pos = 0;
  // Why? => pos += 1;
  // At the first time coloumn 137 we find ozt all the position whos hsa a uppercase, after find out the position,
  // we add a "_", so that the second position dont fit because we add "_" (pos +1), if we found a third uppercase character,
  // e.g "mellowYellowMuted", we add two "_", so on the third run we add tweo to pos,

  positions.forEach((index) => {
    str = insert(str, "_", index + pos);
    pos += 1;
  });
  return str.toUpperCase();
};

/**
 * Inserts a string into a specific position on another string
 * @param main_string The main string
 * @param ins_string The string to be inserted
 * @param pos the position where the inserted string will be
 * @returns the changed string
 */
const insert = function insert(main_string, ins_string, pos) {
  if (typeof pos == "undefined") {
    pos = 0;
  }
  if (typeof ins_string == "undefined") {
    ins_string = "";
  }
  return main_string.slice(0, pos) + ins_string + main_string.slice(pos);
};

/**
 * Replaces all occurrences inside a string
 * @param str The string where the replacement should happen
 * @param find the string to find
 * @param replace the string to replace
 * @returns Returns the string with the replacements
 */
const replaceAll = function (str, find, replace) {
  // If a regex pattern
  if (Object.prototype.toString.call(find).toLowerCase() === "[object regexp]") {
    return str.replace(find, replace);
  }

  // If a string
  return str.replace(new RegExp(find, "g"), replace);
};

export const camelToDashCase = (v) => {
  let ret = "",
    prevLowercase = false,
    prevIsNumber = false;
  for (let s of v) {
    const isUppercase = s.toUpperCase() === s;
    const isNumber = !isNaN(s);
    if (isNumber) {
      if (prevLowercase) {
        // ret += "-";
      }
    } else {
      if (isUppercase && (prevLowercase || prevIsNumber)) {
        ret += "-";
      }
    }
    ret += s;
    prevLowercase = !isUppercase;
    prevIsNumber = isNumber;
  }
  return ret.replace(/-+/g, "-").toLowerCase();
};
